import React, {useState} from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { GetSortPlanResource } from '../resources/GetSortPlanResource';
import { getNodeId, downloadCsvFromUrl} from '../Utility';
import {FormattedMessage, injectIntl} from "react-intl";
import { FileType } from '../enums';
import {FEATURES} from "../constants";
import Loader from "@amzn/meridian/loader";
import Alert from "@amzn/meridian/alert";

const STATUS_CODES = {
    SUCCESS: "SUCCESS",
    SORT_PLAN_GENERATION_FAILED: "SORT_PLAN_GENERATION_FAILED",
    SORT_PLAN_NOT_YET_GENERATED: "SORT_PLAN_NOT_YET_GENERATED",
    SORT_PLAN_IN_PROGRESS : "SORT_PLAN_IN_PROGRESS"
};
const DASHBOARD_LINK = "https://wiki.amazon.com/bin/view/StationTech/Outbound/SortPathGenerator/STAG/SortPlanDashboards/";

export function getBackdropStyle(color) {
    return {
        'position': 'fixed',
        'borderCollapse': 'collapse',
        'textAlign': 'center',
        'backgroundColor': color,
        'zIndex': '9998'
    };
}

export function getAlertStyle() {
    return {
        'position': 'fixed',
        'top': '20px',
        'left': '50%',
        'transform': 'translateX(-50%)',
        'zIndex': '9999',
        'width': '90%',
        'maxWidth': '600px',
        'boxShadow': '0 4px 12px rgba(0, 0, 0, 0.15)'
    };
}

/**
 * Portal component to render alert at the top of the page
 */
const ErrorAlertPortal = ({ message, onClose, intl }) => {
    return ReactDOM.createPortal(
        <React.Fragment>
            <div style={getBackdropStyle('rgba(0, 0, 0, 0.5)')} onClick={onClose}></div>
            <div style={getAlertStyle()}>
                <Alert type="error" dismissible onDismiss={onClose}>
                    <strong>{message}</strong>
                    {' '}
                    {intl.formatMessage({
                        id: 'stow.error.check_dashboard_prefix',
                        defaultMessage: 'Check the'
                    })}
                    {' '}
                    <a href={DASHBOARD_LINK}
                        target="_blank" rel="noopener noreferrer"
                        style={{ color: '#0073bb', textDecoration: 'underline' }}
                    >
                        {intl.formatMessage({
                            id: 'stow.error.dashboard_link_text',
                            defaultMessage: 'dashboard'
                        })}
                    </a>
                    {' '}
                    {intl.formatMessage({
                        id: 'stow.error.check_dashboard_suffix',
                        defaultMessage: 'for more details.'
                    })}
                </Alert>
            </div>
        </React.Fragment>,
        document.body
    );
};

/**
 * Button component for downloading sort plan data in CSV format.
 * Gets the nodeId from query parameters and cycleId from Redux state.
 */
const DownloadSortPlanButton = ({selectedCycle, featureStatus, intl}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const handleCloseAlert = () => {
        setErrorMessage(null);
    };

    const handleClick = async () => {
        try {
            setIsLoading(true);
            const nodeId = getNodeId();
            const cycleId = selectedCycle;
            const fileType = FileType.CSV;
            const response = await GetSortPlanResource(nodeId, cycleId, fileType);
            const { statusCode, sortPlanMetadataPresignedS3Url } = response.data;

            switch (statusCode) {
                case STATUS_CODES.SUCCESS:
                    const today = new Date();
                    const dateStr = today.toISOString().split('T')[0];
                    const filename = `SortPlan-${nodeId}-${cycleId}-${dateStr}.csv`;
                    await downloadCsvFromUrl(sortPlanMetadataPresignedS3Url, filename);
                    break;
                case STATUS_CODES.SORT_PLAN_GENERATION_FAILED:
                    setErrorMessage(intl.formatMessage({
                        id: 'stow.error.sort_plan_not_generated',
                        defaultMessage: 'The sort plan has not been generated yet.'
                    }));
                    break;
                case STATUS_CODES.SORT_PLAN_NOT_YET_GENERATED:
                    setErrorMessage(intl.formatMessage({
                        id: 'stow.error.sort_plan_generation_failed',
                        defaultMessage: 'Sort Plan Generation Failed'
                    }));
                    break;
                case STATUS_CODES.SORT_PLAN_IN_PROGRESS:
                    setErrorMessage(intl.formatMessage({
                        id: 'stow.error.sort_plan_inProgress',
                        defaultMessage: 'Sort Planning is in progress'
                    }));
                    break;
                default:
                    setErrorMessage(intl.formatMessage({
                        id: 'stow.error.unexpected_error',
                        defaultMessage: 'Unexpected Error: Received status code {statusCode}.'
                    }, {
                        statusCode: statusCode
                    }));
            }
        } catch (error) {
            setErrorMessage(intl.formatMessage({
                id: 'stow.error.network_error',
                defaultMessage: 'Failed to fetch sort plan. Please try again.'
            }));
        } finally {
            setIsLoading(false);
        }
    };

    if (!featureStatus || !featureStatus[FEATURES.DOWNLOAD_SORT_PLAN_BUTTON_VISIBILITY]) {
        return null;
    }

    return (
        <React.Fragment>
            {errorMessage && (
                <ErrorAlertPortal
                    message={errorMessage}
                    onClose={handleCloseAlert}
                    intl={intl}
                />
            )}

            <div className="download-sort-plan-container">
                <span
                    onClick={isLoading ? null : handleClick}
                    className={`download-sort-plan-link ${isLoading  ? 'disabled' : ''}`}
                    data-cy="downloadSortPlanBtn"
                >
                    {isLoading ? (
                        <Loader size="small" />
                    ) : (
                        <FormattedMessage id="stow.download_sort_location_plan" defaultMessage="Download-sort-location-plan"/>
                    )}
                </span>
            </div>
        </React.Fragment>
    );
}

const mapStateToProps = (state) => ({
    selectedCycle: state.optionsReducer.options.cycle,
    cycleId: state.optionsReducer.options.cycleId,
    featureStatus: state.configReducer.featureStatus
});

export default injectIntl(withRouter(connect(mapStateToProps)(DownloadSortPlanButton)));


// WEBPACK FOOTER //
// ./src/components/DownloadSortPlanButton.js